import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {CmsLinkComponent, CmsNavigationComponent, CmsNavigationNode, CmsService} from '@spartacus/core';
import {DcpLinkModel} from '../model/dcp-link-model';

const SECTION_HOMEPAGE: string = 'homepage';
const DOMAIN_REGEX: string = '/^(\\/+|\\/+)$/g';

export interface ExtendedCmsLinkComponent extends CmsLinkComponent {
  product?: string;
  productCode?: string;
  category?: string;
  categoryCode?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationComponentService {
  constructor(private cmsService: CmsService) {}

  /**
   * Fetches the data of a NavigationComponent.
   * Entry point for this service.
   *
   * @param navigationComponentId identifier of the Hybris NavigationComponent
   * @return list of the Navigation links returned by hybris for the navigationComponentId
   */
  public fetchComponentData(navigationComponentId: string): Observable<Map<string, Observable<DcpLinkModel>[]>> {
    const componentData$: Observable<CmsNavigationComponent> = this.cmsService.getComponentData(navigationComponentId);

    return componentData$.pipe(map((navComponent) => this.fetchNavNodeData(navComponent)));
  }

  /**
   * Extracts the page section from the given url.
   *
   * @param url from navigation event
   */
  public getSectionFromUrl(url: string): string {
    if (typeof url !== 'string') {
      throw new TypeError(`Expected a string for 'url', but got ${typeof url}`);
    }
    return url.split('?')?.[0]?.replace(DOMAIN_REGEX, '')?.split('/')?.[3] || SECTION_HOMEPAGE;
  }

  /**
   * Collects the information for the nodes containing links returned by hybris and stores them in a map.
   *
   * @param navComponent CmsNavigationComponent to retrieve the NavigationNodes from
   */
  fetchNavNodeData(navComponent: CmsNavigationComponent): Map<string, Observable<DcpLinkModel>[]> {
    const nodes: Map<string, Array<Observable<DcpLinkModel>>> = new Map<string, Array<any>>();
    if (navComponent && navComponent.navigationNode) {
      const navigationNodes: Array<CmsNavigationNode> = navComponent.navigationNode.children;

      navigationNodes.forEach((navNode) => {
        nodes.set(navNode.title, this.getDcpLinks(navNode));
      });
    }
    return nodes;
  }

  /**
   * Collects the information for the links returned by hybris and stores them in an array.
   *
   * @param navNode NavigationNode to retrieve the links from
   */
  private getDcpLinks(navNode: CmsNavigationNode): Array<Observable<DcpLinkModel>> {
    const links: Array<Observable<DcpLinkModel>> = [];
    navNode.entries.forEach((entry) => {
      const navNodeData$: Observable<CmsLinkComponent> = this.cmsService.getComponentData(entry.itemId);
      const link$: Observable<DcpLinkModel> = navNodeData$.pipe(
        first(Boolean),
        map((linkComponent: ExtendedCmsLinkComponent) => this.extendedCmsLinkToDcpLink(linkComponent))
      );
      links.push(link$);
    });
    return links;
  }

  /**
   * Retrieves the url of the CmsLinkComponent
   *
   * @param eLinkComponent contains the information about the link
   * @return a string with the built url
   */
  getUrl(eLinkComponent: ExtendedCmsLinkComponent): string {
    if (eLinkComponent.contentPage && eLinkComponent.contentPageLabelOrId) {
      return `/${eLinkComponent.contentPageLabelOrId.replace('/', '')}`;
    } else if (eLinkComponent.product && eLinkComponent.productCode) {
      return `/product/${eLinkComponent.productCode}`;
    } else if (eLinkComponent.category && eLinkComponent.categoryCode) {
      return `/category/${eLinkComponent.categoryCode}`;
    } else {
      return eLinkComponent.url;
    }
  }

  /**
   * Converts the ExtendedCmsLinkComponent to a DcpLinkModel.
   * DcpLinkModel is needed since returned properties are not defined in CmsLinkComponent interface
   *
   * @param eLinkComponent to convert to DcpLinkModel
   */
  extendedCmsLinkToDcpLink(eLinkComponent: ExtendedCmsLinkComponent): DcpLinkModel {
    return {
      name: eLinkComponent.name,
      linkName: eLinkComponent.linkName,
      // target should be boolean according to interface, CmsService returns a string instead
      target: (eLinkComponent.target as unknown as string) === 'true' ? '_blank' : '_self',
      url: this.getUrl(eLinkComponent),
    };
  }

  mapToArray(m: Map<string, Observable<DcpLinkModel>[]>): Array<Observable<DcpLinkModel>[]> {
    return Array.from(m.values());
  }
}
